import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { catalogosService } from '../../services/catalogosService';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatCurrency } from '../../utils/formatters';
import RecetaArticuloSearchDialog, {
  mapProductoToRecetaArticulo,
  RecetaArticuloSeleccionado,
} from './RecetaArticuloSearchDialog';
import RecetaArticuloCodigoField from './RecetaArticuloCodigoField';

export interface RecetaComponenteLinea {
  id: string;
  codigoArticulo: string;
  descripcion: string;
  unidadMedida: string;
  cantidad: number;
  costo: number;
  notas: string;
}

interface Props {
  lineas: RecetaComponenteLinea[];
  onLineaChange: (id: string, field: keyof RecetaComponenteLinea, value: string | number) => void;
  onLineaFilled: (id: string, data: Partial<RecetaComponenteLinea>) => void;
  onEliminarLinea: (id: string) => void;
  onAgregarLineaVacia: () => void;
  onMoverLinea?: (id: string, direccion: -1 | 1) => void;
  readOnly?: boolean;
  tiposArticulo?: string;
  mensajeTipoInvalido?: string;
}

const NAV_COLS = ['codigoArticulo', 'descripcion', 'cantidad', 'notas'] as const;
const MSG_CODIGO_INEXISTENTE = 'No existe en el maestro de artículos';
const TIPOS_COMPONENTES_DEFAULT = 'MP,PT,SE,SER';
const MSG_TIPO_COMPONENTE =
  'El componente debe ser Materia Prima, Producto Terminado, Semi Elaborado o Servicio';

const CampoConTooltip: React.FC<{ title?: string | null; children: React.ReactElement }> = ({
  title,
  children,
}) => {
  const texto = String(title ?? '').trim();
  return (
    <Tooltip
      title={texto}
      disableHoverListener={!texto}
      placement="top"
      enterDelay={400}
      slotProps={{ tooltip: { sx: { maxWidth: 420, whiteSpace: 'pre-wrap' } } }}
    >
      <Box component="span" sx={{ display: 'block', width: '100%' }}>
        {children}
      </Box>
    </Tooltip>
  );
};

const lineaArticuloResuelto = (linea: RecetaComponenteLinea) =>
  Boolean(String(linea.descripcion ?? '').trim());

export const decimalesUnidad = (
  unidadMedida: string,
  porCodigo: Map<string, number>,
  catalogoListo: boolean,
) => {
  const codigo = unidadMedida.trim().toUpperCase();
  if (!codigo || codigo === '—') return catalogoListo ? 0 : 3;
  if (!catalogoListo) return 3;
  return porCodigo.get(codigo) ?? 0;
};

const formatearCantidad = (value: number, decimales: number) => {
  if (!Number.isFinite(value)) return '';
  const tope = Math.max(0, decimales);
  const factor = 10 ** tope;
  const redondeado = tope === 0 ? Math.trunc(value) : Math.round(value * factor) / factor;
  return String(redondeado);
};

const sanitizarCantidad = (raw: string, decimales: number) => {
  const limpio = raw.replace(',', '.').replace(/[^\d.]/g, '');
  const partes = limpio.split('.');
  const entera = partes[0] ?? '';
  if (decimales <= 0) return entera;
  if (partes.length === 1) return entera;
  const fraccion = partes.slice(1).join('').slice(0, decimales);
  const terminaEnSeparador = raw.endsWith('.') || raw.endsWith(',');
  if (!fraccion && terminaEnSeparador) return `${entera}.`;
  return fraccion ? `${entera}.${fraccion}` : entera;
};

export const CantidadUmField: React.FC<{
  value: number;
  decimales: number;
  readOnly?: boolean;
  label?: string;
  unidadMedida?: string;
  sx?: object;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  onChange: (value: number) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}> = ({ value, decimales, readOnly, label, unidadMedida, sx, inputProps, onChange, onKeyDown }) => {
  const [focused, setFocused] = useState(false);
  const [texto, setTexto] = useState(() => formatearCantidad(value, decimales));

  useEffect(() => {
    if (!focused) setTexto(formatearCantidad(value, decimales));
  }, [value, decimales, focused]);

  const publicar = (raw: string) => {
    const normalizado = raw.endsWith('.') ? raw.slice(0, -1) : raw;
    if (!normalizado) {
      onChange(0);
      return;
    }
    const n = Number(normalizado);
    onChange(Number.isFinite(n) ? Math.max(0, n) : 0);
  };

  const um = String(unidadMedida ?? '').trim();
  const mostrarUm = Boolean(um && um !== '—');

  return (
    <TextField
      size="small"
      fullWidth
      type="text"
      label={label}
      value={focused ? texto : formatearCantidad(value, decimales)}
      onFocus={() => {
        setFocused(true);
        setTexto(formatearCantidad(value, decimales));
      }}
      onBlur={() => {
        setFocused(false);
        const normalizado = texto.endsWith('.') ? texto.slice(0, -1) : texto;
        const n = normalizado ? Number(normalizado) : 0;
        const final = Number.isFinite(n) ? Math.max(0, n) : 0;
        onChange(final);
        setTexto(formatearCantidad(final, decimales));
      }}
      onChange={(e) => {
        const siguiente = sanitizarCantidad(e.target.value, decimales);
        setTexto(siguiente);
        publicar(siguiente);
      }}
      onKeyDown={onKeyDown}
      inputProps={{
        ...inputProps,
        inputMode: decimales > 0 ? 'decimal' : 'numeric',
      }}
      InputProps={{
        readOnly,
        endAdornment: mostrarUm ? (
          <InputAdornment position="end">
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, userSelect: 'none' }}>
              {um}
            </Typography>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 }, ...sx }}
    />
  );
};

const RecetaComponentesGrid: React.FC<Props> = ({
  lineas,
  onLineaChange,
  onLineaFilled,
  onEliminarLinea,
  onAgregarLineaVacia,
  onMoverLinea,
  readOnly = false,
  tiposArticulo = TIPOS_COMPONENTES_DEFAULT,
  mensajeTipoInvalido = MSG_TIPO_COMPONENTE,
}) => {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [codigoErrors, setCodigoErrors] = useState<Record<string, string>>({});
  const [lookupLineaId, setLookupLineaId] = useState<string | null>(null);
  const [lookupTerm, setLookupTerm] = useState('');
  const [decimalesPorUnidad, setDecimalesPorUnidad] = useState<Map<string, number>>(() => new Map());
  const [catalogoUnidadesListo, setCatalogoUnidadesListo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    catalogosService.getUnidadesMedida(1, 1000).then((result) => {
      if (cancelled) return;
      const mapa = new Map<string, number>();
      (result.items ?? []).forEach((unidad) => {
        const codigo = String(unidad.idUnidadMedida ?? '').trim().toUpperCase();
        if (!codigo) return;
        const decimales = Number(unidad.cantDecimales);
        mapa.set(codigo, Number.isFinite(decimales) && decimales > 0 ? Math.trunc(decimales) : 0);
      });
      setDecimalesPorUnidad(mapa);
      setCatalogoUnidadesListo(true);
    }).catch(() => {
      if (!cancelled) setCatalogoUnidadesListo(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const tiposSet = useMemo(
    () =>
      new Set(
        tiposArticulo
          .split(',')
          .map((t) => t.trim().toUpperCase())
          .filter(Boolean),
      ),
    [tiposArticulo],
  );

  const clearCodigoError = useCallback((id: string) => {
    setCodigoErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const marcarCodigoInexistente = useCallback(
    (id: string, msg = MSG_CODIGO_INEXISTENTE) => {
      setCodigoErrors((prev) => ({ ...prev, [id]: msg }));
      onLineaFilled(id, { descripcion: '', unidadMedida: '', costo: 0 });
    },
    [onLineaFilled],
  );

  const focusCell = useCallback((rowIdx: number, colName: string) => {
    const el = tableRef.current?.querySelector(
      `[data-row="${rowIdx}"][data-col="${colName}"]`,
    ) as HTMLInputElement | null;
    el?.focus();
    el?.select();
  }, []);

  const aplicarArticulo = useCallback(
    (id: string, item: RecetaArticuloSeleccionado, rowIdx: number) => {
      clearCodigoError(id);
      onLineaFilled(id, {
        codigoArticulo: item.codigoArticulo,
        descripcion: item.descripcion,
        unidadMedida: item.unidadMedida,
        costo: item.costo,
      });
      setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
    },
    [clearCodigoError, onLineaFilled, focusCell],
  );

  const handleResolveCodigo = useCallback(
    async (id: string, codigo: string) => {
      if (!codigo.trim() || readOnly) return;
      setResolvingId(id);
      const rowIdx = lineas.findIndex((l) => l.id === id);

      try {
        const producto = await productoService.getProducto(codigo.trim(), OPTIONS_PRODUCTO_OPERATIVO);
        const tipo = String(producto.tipoArticulo ?? '').trim().toUpperCase();
        if (tiposSet.size > 0 && !tiposSet.has(tipo)) {
          marcarCodigoInexistente(id, mensajeTipoInvalido);
          setLookupLineaId(id);
          setLookupTerm(codigo.trim());
          return;
        }
        aplicarArticulo(id, mapProductoToRecetaArticulo(producto), rowIdx);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const apiMsg = getApiErrorMessage(err, '');
        if (status === 409 && apiMsg) {
          marcarCodigoInexistente(id, apiMsg);
        } else {
          marcarCodigoInexistente(id);
        }
        setLookupLineaId(id);
        setLookupTerm(codigo.trim());
      } finally {
        setResolvingId(null);
      }
    },
    [lineas, readOnly, aplicarArticulo, marcarCodigoInexistente, tiposSet, mensajeTipoInvalido],
  );

  const handleCodigoBlur = useCallback(
    async (id: string, codigo: string) => {
      const trimmed = codigo.trim();
      if (!trimmed || readOnly) return;
      const linea = lineas.find((l) => l.id === id);
      if (!linea || lineaArticuloResuelto(linea)) return;
      await handleResolveCodigo(id, trimmed);
    },
    [lineas, readOnly, handleResolveCodigo],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, rowIdx: number, colName: string) => {
      if (readOnly) return;
      const id = lineas[rowIdx]?.id;

      if (colName !== 'codigoArticulo') {
        if (e.key === 'F4' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l')) {
          e.preventDefault();
          if (id) {
            const linea = lineas[rowIdx];
            setLookupTerm(linea.codigoArticulo || linea.descripcion);
            setLookupLineaId(id);
          }
          return;
        }
      }

      const colIdx = NAV_COLS.indexOf(colName as (typeof NAV_COLS)[number]);
      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        const isLastCol = colIdx === NAV_COLS.length - 1;
        if (!isLastCol) {
          focusCell(rowIdx, NAV_COLS[colIdx + 1]);
        } else if (rowIdx < lineas.length - 1) {
          focusCell(rowIdx + 1, NAV_COLS[0]);
        } else {
          onAgregarLineaVacia();
          setTimeout(() => focusCell(rowIdx + 1, NAV_COLS[0]), 60);
        }
      } else if (e.key === 'Enter' && colName !== 'codigoArticulo') {
        e.preventDefault();
        if (rowIdx < lineas.length - 1) {
          focusCell(rowIdx + 1, NAV_COLS[0]);
        } else {
          onAgregarLineaVacia();
          setTimeout(() => focusCell(rowIdx + 1, NAV_COLS[0]), 60);
        }
      }
    },
    [lineas, focusCell, onAgregarLineaVacia, readOnly],
  );

  const handleChange = (id: string, field: keyof RecetaComponenteLinea, raw: string) => {
    if (field === 'codigoArticulo') {
      clearCodigoError(id);
      const linea = lineas.find((l) => l.id === id);
      onLineaChange(id, field, raw);
      if (linea && lineaArticuloResuelto(linea)) {
        onLineaFilled(id, { descripcion: '', unidadMedida: '', costo: 0 });
      }
      return;
    }
    if (field === 'cantidad' || field === 'costo') {
      const v = parseFloat(raw.replace(',', '.'));
      onLineaChange(id, field, Number.isNaN(v) ? 0 : v);
      return;
    }
    onLineaChange(id, field, raw);
  };

  const cellSx = {
    p: '3px 4px',
    border: 0,
    borderBottom: '1px solid',
    borderColor: 'divider',
    verticalAlign: 'middle',
  };
  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 1 } };
  const textInputProps = (rowIdx: number, col: string) => ({
    'data-row': rowIdx,
    'data-col': col,
    style: { padding: '4px 6px', fontSize: '0.82rem' },
  });
  const numInputProps = (rowIdx: number, col: string) => ({
    'data-row': rowIdx,
    'data-col': col,
    style: { textAlign: 'right' as const, padding: '4px 6px', fontSize: '0.82rem' },
    min: 0,
    step: 'any',
  });
  const headerCellSx = { fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap' } as const;

  const lookupLinea = lineas.find((l) => l.id === lookupLineaId);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Componentes (materias primas)</Typography>
        {!readOnly && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAgregarLineaVacia}>
            Agregar línea
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" ref={tableRef} sx={{ tableLayout: 'fixed', minWidth: 1020 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ ...headerCellSx, width: 72 }} align="center">
                  Orden
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: 140 }}>Código</TableCell>
                <TableCell sx={{ ...headerCellSx, width: '22%' }}>Descripción</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 70 }}>U/M</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 100 }} align="right">
                  Cantidad
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: 100 }} align="right">
                  Costo
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: 110 }} align="right">
                  Subtotal
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: '22%' }}>Notas / proc.</TableCell>
                <TableCell sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                    Agregá una línea y buscá el artículo con la lupa o F4.
                  </TableCell>
                </TableRow>
              )}
              {lineas.map((linea, rowIdx) => {
                const isResolving = resolvingId === linea.id;
                const subtotal = (Number(linea.cantidad) || 0) * (Number(linea.costo) || 0);
                return (
                  <TableRow key={linea.id} hover>
                    <TableCell sx={cellSx} align="center">
                      {!readOnly && onMoverLinea ? (
                        <Stack direction="row" spacing={0} justifyContent="center">
                          <Tooltip title="Subir">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Subir línea ${rowIdx + 1}`}
                                disabled={rowIdx === 0}
                                onClick={() => onMoverLinea(linea.id, -1)}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Bajar">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Bajar línea ${rowIdx + 1}`}
                                disabled={rowIdx >= lineas.length - 1}
                                onClick={() => onMoverLinea(linea.id, 1)}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {rowIdx + 1}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{ position: 'relative' }}>
                        <CampoConTooltip title={linea.codigoArticulo}>
                          <RecetaArticuloCodigoField
                            value={linea.codigoArticulo}
                            articuloResuelto={lineaArticuloResuelto(linea)}
                            resolving={isResolving}
                            readOnly={readOnly}
                            error={Boolean(codigoErrors[linea.id])}
                            tiposArticulo={tiposArticulo}
                            inputProps={textInputProps(rowIdx, 'codigoArticulo')}
                            onChange={(raw) => handleChange(linea.id, 'codigoArticulo', raw)}
                            onSelect={(item) => aplicarArticulo(linea.id, item, rowIdx)}
                            onResolveExact={(codigo) => void handleCodigoBlur(linea.id, codigo)}
                            onOpenDialog={() => {
                              setLookupTerm(linea.codigoArticulo || linea.descripcion);
                              setLookupLineaId(linea.id);
                            }}
                            onKeyDownExtra={(e) => handleKeyDown(e, rowIdx, 'codigoArticulo')}
                            sx={{ ...inputSx, m: 0, '& input': { fontFamily: 'monospace' } }}
                          />
                        </CampoConTooltip>
                        {codigoErrors[linea.id] ? (
                          <Typography
                            component="span"
                            variant="caption"
                            color="error"
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              top: '100%',
                              mt: 0.25,
                              fontSize: '0.65rem',
                              lineHeight: 1.1,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {codigoErrors[linea.id]}
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <CampoConTooltip title={linea.descripcion}>
                        <TextField
                          size="small"
                          fullWidth
                          value={linea.descripcion}
                          onChange={(e) => handleChange(linea.id, 'descripcion', e.target.value)}
                          onKeyDown={(e) =>
                            handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, rowIdx, 'descripcion')
                          }
                          inputProps={textInputProps(rowIdx, 'descripcion')}
                          InputProps={{ readOnly }}
                          sx={inputSx}
                        />
                      </CampoConTooltip>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {linea.unidadMedida || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <CantidadUmField
                        value={Number(linea.cantidad) || 0}
                        decimales={decimalesUnidad(linea.unidadMedida, decimalesPorUnidad, catalogoUnidadesListo)}
                        readOnly={readOnly}
                        inputProps={numInputProps(rowIdx, 'cantidad')}
                        onChange={(cantidad) => onLineaChange(linea.id, 'cantidad', cantidad)}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 'cantidad')}
                      />
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Typography variant="body2">{formatCurrency(Number(linea.costo) || 0)}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(subtotal)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <CampoConTooltip title={linea.notas}>
                        <TextField
                          size="small"
                          fullWidth
                          value={linea.notas}
                          placeholder="Procedimiento…"
                          onChange={(e) => handleChange(linea.id, 'notas', e.target.value)}
                          onKeyDown={(e) =>
                            handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, rowIdx, 'notas')
                          }
                          inputProps={textInputProps(rowIdx, 'notas')}
                          InputProps={{ readOnly }}
                          sx={inputSx}
                        />
                      </CampoConTooltip>
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      {!readOnly && (
                        <IconButton
                          size="small"
                          color="error"
                          disabled={lineas.length <= 1}
                          onClick={() => onEliminarLinea(linea.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <RecetaArticuloSearchDialog
        open={Boolean(lookupLineaId)}
        title="Buscar componente"
        tiposArticulo={tiposArticulo}
        initialSearchTerm={lookupTerm || lookupLinea?.codigoArticulo || lookupLinea?.descripcion || ''}
        onClose={() => setLookupLineaId(null)}
        onSelect={(item) => {
          if (!lookupLineaId) return;
          const rowIdx = lineas.findIndex((l) => l.id === lookupLineaId);
          aplicarArticulo(lookupLineaId, item, rowIdx);
          setLookupLineaId(null);
        }}
      />
    </Box>
  );
};

export default RecetaComponentesGrid;
